import { FundingProduct } from '@frontend/components/agromarket/core/access-control'
import breadcrumbsMessages from '@/components/ui/Breadcrumbs/messages.json'
import type { IUserGtmInfoPayload } from '@/interfaces'
import type { IFinancingProductInfo } from '@/logic/metrika/financing/types'
import { messages as advancePaymentOnFutureHarvestPageMessages } from '@/pages/AdvancePaymentOnFutureHarvest/messages'
import { messages as expressCreditAlfaBankMessages } from '@/pages/ExpressCreditAlfaBank/messages'
import { getEventContext } from '@/pages/Financing/gtmHelpers'
import { messages as interleasingPageMessages } from '@/pages/Interleasing/messages'
import type { RootState } from '@/redux/interfaces'
import type { ESubmitFormStatusWithGtm } from '@/types'
import { EFinancingOfferRoute, EFinancingOfferType } from '@/types'
import { messages as financingMessages } from './financing/messages'
import { getUserGtmInfo, pushFormattedGtmEvent } from './helpers'
import type { IFormattedGtmEventPayload } from './interfaces'

export enum EIntersectionState {
  intersected = 'intersected',
  didNotIntersect = 'didNotIntersect',
}

export const basePageViewingGtmEvent: Omit<IFormattedGtmEventPayload, 'eventContext'> = {
  event: 'userEvent',
  pagePart: 'content',
  blockName: null,
  eventCategory: 'interactions',
  eventAction: 'viewing',
  eventLabel: 'pageView',
  eventStatus: 'success',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
}

export const baseScrollGtmEvent: Omit<IFormattedGtmEventPayload, 'eventLabel'> = {
  event: 'userEvent',
  pagePart: 'content',
  eventCategory: 'interactions',
  eventAction: 'scrolling',
  eventContext: null,
  eventStatus: 'success',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
  blockName: null,
}

export const baseFocusGtmEvent: Omit<IFormattedGtmEventPayload, 'blockName' | 'eventLabel'> = {
  event: 'userEvent',
  pagePart: 'content',
  eventCategory: 'interactions',
  eventAction: 'click',
  eventContext: null,
  eventStatus: 'success',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
}

export const baseChangeGtmEvent: Omit<IFormattedGtmEventPayload, 'blockName' | 'eventLabel'> = {
  event: 'userEvent',
  pagePart: 'content',
  eventCategory: 'interactions',
  eventAction: 'input',
  eventContext: null,
  eventStatus: 'success',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
}

export const baseClickGtmEvent: Omit<IFormattedGtmEventPayload, 'blockName' | 'eventLabel'> = {
  event: 'userEvent',
  pagePart: 'content',
  eventCategory: 'interactions',
  eventAction: 'click',
  eventContext: null,
  eventStatus: 'success',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
}

export const baseFormSubmitGtmEvent: Omit<IFormattedGtmEventPayload, 'blockName' | 'eventStatus'> = {
  event: 'userEvent',
  pagePart: 'content',
  eventCategory: 'conversions',
  eventAction: 'backgroundProcess',
  eventLabel: 'processFormSent',
  eventContext: null,
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
}

export const financingProductGtmInfo: Partial<Record<FundingProduct, IFinancingProductInfo>> = {
  [EFinancingOfferType.ALFA_EXPRESS_LOAN]: {
    product: expressCreditAlfaBankMessages.header.title,
    productNameOnLending: expressCreditAlfaBankMessages.header.title,
    bank: 'Альфа банк',
  },
  [FundingProduct.TINKOFF_REVOLVING_LOAN]: {
    product: breadcrumbsMessages.ru['breadcrumbs.financingOffer.express'],
    productNameOnLending: breadcrumbsMessages.ru['breadcrumbs.financingOffer.replenishmentRevolving'],
    bank: 'Тинькофф банк',
  },
  [FundingProduct.EXPRESS_LOAN]: {
    product: financingMessages.product.expressLoan,
    productNameOnLending: breadcrumbsMessages.ru['breadcrumbs.financingOffer.express'],
    bank: 'ВТБ',
  },
  [EFinancingOfferType.REVOLVING_LOAN]: {
    product: financingMessages.product.revolvingLoan,
    productNameOnLending: breadcrumbsMessages.ru['breadcrumbs.financingOffer.revolving'],
    bank: 'ВТБ',
  },
  [FundingProduct.ADVANCE_FUNDING]: {
    product: breadcrumbsMessages.ru['breadcrumbs.financingOffer.advancePaymentOnFutureHarvest'],
    productNameOnLending: advancePaymentOnFutureHarvestPageMessages.header.title,
    bank: 'Деметра холдинг',
  },
  [FundingProduct.LEASING_INTERLEASING]: {
    product: interleasingPageMessages.header.title,
    productNameOnLending: interleasingPageMessages.header.title,
    bank: 'Интерлизинг',
  },
  [FundingProduct.LEASING_VTB]: {
    product: financingMessages.product.vtbLeasing,
    productNameOnLending: breadcrumbsMessages.ru['breadcrumbs.financingOffer.transportAndSpecialEquipmentLeasing'],
    bank: 'ВТБ Лизинг',
  },
  [FundingProduct.LEASING_RAL]: {
    product: financingMessages.product.transportAndSpecialEquipmentLeasing,
    productNameOnLending: interleasingPageMessages.header.title,
    bank: 'Росагролизинг',
  },
  [FundingProduct.INVESTMENT]: {
    product: financingMessages.product.investmentLoan,
    productNameOnLending: breadcrumbsMessages.ru['breadcrumbs.financingOffer.investment'],
    bank: 'ВТБ',
  },
  [FundingProduct.LEASING_ALFA]: {
    product: financingMessages.product.alfaLeasing,
    bank: 'Альфа лизинг',
  },
}

export const financingSection: Partial<Record<FundingProduct, string>> = {
  [FundingProduct.ALFA_EXPRESS_LOAN]: financingMessages.offerGroup.express.title,
  [FundingProduct.TINKOFF_REVOLVING_LOAN]: financingMessages.offerGroup.express.title,
  [FundingProduct.REVOLVING_LOAN]: financingMessages.offerGroup.replenish.title,
  [FundingProduct.ADVANCE_FUNDING]: financingMessages.offerGroup.replenish.title,
  [FundingProduct.LEASING_ALFA]: financingMessages.offerGroup.leasing.title,
  [FundingProduct.LEASING_INTERLEASING]: financingMessages.offerGroup.leasing.title,
  [FundingProduct.LEASING_VTB]: financingMessages.offerGroup.leasing.title,
  [FundingProduct.LEASING_RAL]: financingMessages.offerGroup.leasing.title,
  [FundingProduct.INVESTMENT]: financingMessages.offerGroup.capital.title,
}

export const financingProduct: Partial<Record<EFinancingOfferRoute, IFinancingProductInfo>> = {
  [EFinancingOfferRoute.expressCreditAlfaBank]: financingProductGtmInfo[FundingProduct.ALFA_EXPRESS_LOAN],
  [EFinancingOfferRoute.revolvingTinkoff]: financingProductGtmInfo[FundingProduct.TINKOFF_REVOLVING_LOAN],
  [EFinancingOfferRoute.expressCredit]: financingProductGtmInfo[FundingProduct.EXPRESS_LOAN],
  [EFinancingOfferRoute.revolving]: financingProductGtmInfo[FundingProduct.REVOLVING_LOAN],
  [EFinancingOfferRoute.oborotSber]: financingProductGtmInfo[FundingProduct.REVOLVING_LOAN_SBER],
  [EFinancingOfferRoute.advancePaymentOnFutureHarvest]: financingProductGtmInfo[FundingProduct.ADVANCE_FUNDING],
  [EFinancingOfferRoute.FinancingAgentPage]: financingProductGtmInfo[FundingProduct.AGENT],
  [EFinancingOfferRoute.interleasing]: financingProductGtmInfo[FundingProduct.LEASING_INTERLEASING],
  [EFinancingOfferRoute.transportAndSpecialEquipmentLeasing]: financingProductGtmInfo[FundingProduct.LEASING_VTB],
  [EFinancingOfferRoute.agriculturalMachinesAndEquipmentLeasing]: financingProductGtmInfo[FundingProduct.LEASING_RAL],
  [EFinancingOfferRoute.investmentCredit]: financingProductGtmInfo[FundingProduct.INVESTMENT],
  [EFinancingOfferRoute.stoneXxiLeasing]: financingProductGtmInfo[FundingProduct.LEASING_STONE_XXI],
  [EFinancingOfferRoute.fleetFinanceLeasing]: financingProductGtmInfo[FundingProduct.LEASING_FLEET_FINANCE],
}

export const baseSubmitFormEventPayload: Omit<IFormattedGtmEventPayload, 'blockName' | 'eventStatus' | 'eventLabel' | 'eventContext'> = {
  event: 'userEvent',
  pagePart: 'content',
  eventCategory: 'conversions',
  eventAction: 'backgroundProcess',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
  userSegment: null,
}

export const submitButtonEventPayload: IFormattedGtmEventPayload = {
  event: 'userEvent',
  pagePart: 'content',
  blockName: 'blockLoanApplicationForm',
  eventCategory: 'interactions',
  eventAction: 'click',
  eventLabel: 'buttonSendRequest',
  eventContext: null,
  eventStatus: 'success',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
}

export const submitTinkoffCreditFormEventPayload: IFormattedGtmEventPayload = {
  ...baseFormSubmitGtmEvent,
  pagePart: 'content',
  eventCategory: 'interactions',
  eventAction: 'click',
  blockName: 'blockLoanApplicationForm',
  eventLabel: 'buttonSendRequest',
  eventStatus: 'success',
}

export enum EScrollEventBlockId {
  bannerPrepayment = 'bannerPrepayment',
  expressLoan = 'expressLoan',
  workingLoan = 'workingLoan',
  advancePaymentOnBail = 'advancePaymentOnBail',
  agriculturalLeasing = 'agriculturalLeasing',
  automobileLeasing = 'automobileLeasing',
  investmentLoan = 'investmentLoan',
  individualConditionsVTB = 'individualConditionsVTB',
  lendingTerms = 'lendingTerms',
  investSber = 'investSber',
}

export enum EClickEventBlockId {
  blockStagesOfReceivingFunds = 'blockStagesOfReceivingFunds',
  blockPersonalData = 'blockPersonalData',
}
export enum ETinkoffFormGtmFieldName {
  agreement = 'agreement',
}

export const baseGtmScrollEvent: Omit<IFormattedGtmEventPayload, 'eventLabel'> = {
  event: 'userEvent',
  pagePart: 'content',
  blockName: null,
  eventCategory: 'interactions',
  eventAction: 'scrolling',
  eventContext: null,
  eventStatus: 'success',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
}

export const GtmScrollEventPayload: Partial<Record<EScrollEventBlockId, IFormattedGtmEventPayload>> = {
  [EScrollEventBlockId.bannerPrepayment]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollBannerPrepayment',
  },
  [EScrollEventBlockId.expressLoan]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollExpressLoan',
  },
  [EScrollEventBlockId.workingLoan]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollWorkingLoan',
  },
  [EScrollEventBlockId.advancePaymentOnBail]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollAdvancePaymentOnBail',
  },
  [EScrollEventBlockId.agriculturalLeasing]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollAgriculturalLeasing',
  },
  [EScrollEventBlockId.automobileLeasing]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollAutomobileLeasing',
  },
  [EScrollEventBlockId.investmentLoan]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollInvestmentLoan',
  },
  [EScrollEventBlockId.individualConditionsVTB]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollIndividualConditionsVTB',
  },
  [EScrollEventBlockId.lendingTerms]: {
    ...baseGtmScrollEvent,
    eventLabel: 'scrollLendingTerms',
  },
}

export const baseEventClick: Omit<IFormattedGtmEventPayload, 'blockName' | 'eventLabel'> = {
  event: 'userEvent',
  pagePart: 'content',
  eventCategory: 'interactions',
  eventAction: 'click',
  eventContext: null,
  eventStatus: 'success',
  eventEcommerce: null,
  eventNonInteraction: '0',
  firingOptions: 'oncePerEvent',
  ecommerce: null,
}

export const FinancingClickGtmEventPayload: Record<EClickEventBlockId, IFormattedGtmEventPayload> = {
  [EClickEventBlockId.blockStagesOfReceivingFunds]: {
    ...baseEventClick,
    blockName: '111',
    eventLabel: 'buttonSendRequest',
  },
  [EClickEventBlockId.blockPersonalData]: {
    ...baseEventClick,
    blockName: 'blockLoanApplicationForm',
    eventLabel: 'agreementPersonalData',
  },
}

export const TinkoffFormFocusingGtmEventPayload: Record<ETinkoffFormGtmFieldName, IFormattedGtmEventPayload> = {
  [ETinkoffFormGtmFieldName.agreement]: {
    ...baseFocusGtmEvent,
    blockName: 'blockLoanApplicationForm',
    eventLabel: 'checkboxPersonalData',
  },
}

export enum EClickVTBEventBlockId {
  transportAndSpecialEquipmentLeasing = 'transportAndSpecialEquipmentLeasing',
  selectTransport = 'selectTransport',
  leasingCalculator = 'leasingCalculator',
  stagesOfReceivingFunds = 'stagesOfReceivingFunds',
  specialOffers = 'specialOffers',
  truckLeasing = 'truckLeasing',
  leasingApplicationForm = 'leasingApplicationForm',
}

export const VTBLeasingClickGtmEventPayload: Record<EClickVTBEventBlockId, IFormattedGtmEventPayload> = {
  [EClickVTBEventBlockId.transportAndSpecialEquipmentLeasing]: {
    ...baseClickGtmEvent,
    blockName: 'blockTransportAndSpecialEquipmentLeasing',
    eventLabel: 'buttonSubmitApplication',
  },
  [EClickVTBEventBlockId.selectTransport]: {
    ...baseClickGtmEvent,
    blockName: 'blockSelectTransport',
    eventLabel: 'buttonSendRequest',
  },
  [EClickVTBEventBlockId.leasingCalculator]: {
    ...baseClickGtmEvent,
    blockName: 'blockLeasingCalculator',
    eventLabel: 'buttonSendRequest',
  },
  [EClickVTBEventBlockId.stagesOfReceivingFunds]: {
    ...baseClickGtmEvent,
    blockName: 'blockStagesOfReceivingFunds',
    eventLabel: 'buttonSendRequest',
  },
  [EClickVTBEventBlockId.specialOffers]: {
    ...baseClickGtmEvent,
    blockName: 'blockSpecialOffers',
    eventLabel: 'buttonSendRequest',
  },
  [EClickVTBEventBlockId.truckLeasing]: {
    ...baseClickGtmEvent,
    blockName: 'blockTruckLeasing',
    eventLabel: 'buttonSendRequest',
  },
  [EClickVTBEventBlockId.leasingApplicationForm]: {
    ...baseClickGtmEvent,
    blockName: 'blockLeasingApplicationForm',
    eventLabel: 'buttonSendRequest',
  },
}

export const sendSubmitFormStatusEventWithUserInfo = (
  productId: FundingProduct,
  eventStatus: ESubmitFormStatusWithGtm | string,
  userInfo: IUserGtmInfoPayload,
  pathname?: string,
) => {
  const eventPayload = {
    ...baseSubmitFormEventPayload,
    blockName: 'blockLoanApplicationForm',
    eventLabel: 'loanApplicationFormSent',
    eventContext: getEventContext(financingSection[productId], financingProductGtmInfo[productId], pathname),
    ...userInfo,
    eventStatus,
  }

  pushFormattedGtmEvent(eventPayload)
}

export const sendSubmitFormStatusEvent = (
  productId: FundingProduct,
  eventStatus: ESubmitFormStatusWithGtm | string,
  state: RootState,
  pathname?: string,
) => {
  sendSubmitFormStatusEventWithUserInfo(productId, eventStatus, getUserGtmInfo()(state), pathname)
}
